import { useCallback } from 'react';
import { ISignal, useCustomFetch } from "../custom-fetch";
import IReservation from '../shared/IReservation';
import IReserve from '../shared/IReserve';

interface IGetReservation extends ISignal {
  irn?: string;
  refNo: string;
}

export const useReservationService = () => {
  const customFetch = useCustomFetch();

  const fetchReservations = useCallback(async ({ signal, status, year, month }: { signal: ISignal['signal'], status?: string, year?: number, month?: number }) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    const [data] = await customFetch<IReservation[]>(`/json/reservations/list${query}`, { signal });
    return data;
  }, [customFetch]);

  const getReservation = useCallback(async ({ signal, irn, refNo }: IGetReservation) => {
    const irnParam = irn ? `irn=${encodeURIComponent(irn)}&` : '';
    const [data] = await customFetch<IReservation>(`/json/reservations?${irnParam}refNo=${encodeURIComponent(refNo)}`, { signal });
    return data;
  }, [customFetch]);

  const saveReservation = useCallback(async (reservation: IReserve) => {
    const [data] = await customFetch<any>("/json/reservations/add", {
      method: "POST",
      body: JSON.stringify(reservation),
    });
    return (data.reservation ? data.reservation : data) as IReservation;
  }, [customFetch]);

  const updateReservation = useCallback(async (reservation: IReservation) => {
    const [data] = await customFetch<IReservation>("/json/reservations/update", {
      method: "POST",
      body: JSON.stringify(reservation),
    });
    return data;
  }, [customFetch]);

  const deleteReservation = useCallback(async (reservation: any) => {
    const [result] = await customFetch<boolean>("/json/reservations/delete", {
      method: "POST",
      body: JSON.stringify(reservation),
    });
    return result;
  }, [customFetch]);

  const cancelReservation = useCallback(async (reservation: IReservation) => {
    const [data] = await customFetch<any>("/json/reservations/cancel", {
      method: "POST",
      body: JSON.stringify(reservation),
    });
    return data.reservation ? data.reservation as IReservation : undefined;
  }, [customFetch]);

  const getRefNo = useCallback(async (prefix: string) => {
    const [data] = await customFetch<string>(`/json/reservations/number?prefix=${encodeURIComponent(prefix)}`);
    return data;
  }, [customFetch]);

  const validateRefNo = useCallback(async (refNo: string, previousRefNo: string) => {
    const [result] = await customFetch<string>("/json/reservations/validaterefno", {
      method: "POST",
      body: JSON.stringify({ refNo, previousRefNo }),
    });
    return result;
  }, [customFetch]);

  const exportReservations = useCallback(async () => {
    const [data] = await customFetch<any>("/json/reservations/export");
    return data;
  }, [customFetch]);

  return {
    fetchReservations,
    getReservation,
    saveReservation,
    updateReservation,
    deleteReservation,
    cancelReservation,
    getRefNo,
    validateRefNo,
    exportReservations
  };
};